import { Avatar, Link } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import Flex from "@/components/Flex";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { applyDefaultColumnSettings } from "@/utils/utils";
import {
  CHECK_IN_AMOUNT_CURRENCY,
  CHECK_IN_COLUMN_LABEL,
  CHECK_IN_PAYMENT_STATUS_LABEL,
  CHECK_IN_ROW_ACTION_LABEL,
  CHECK_IN_TABLE_ROW_HEIGHT,
} from "../group-visit-constant";
import { CheckInPatient, CheckInRowAction } from "../group-visit-types";
import {
  getCheckInRowActions,
  getCheckInStatusVariant,
  getInitials,
} from "../group-visit-utils";

export type CheckInPatientTableProps = {
  patients: CheckInPatient[];
  isLoading?: boolean;
  onRowAction: (action: CheckInRowAction, patient: CheckInPatient) => void;
  canEdit?: boolean;
};

const AVATAR_SIZE = 26;

/**
 * Check-in roster — one row per enrolled patient with its payment state.
 *
 * @author Rajkumar Rathod
 */
const CheckInPatientTable = ({
  patients,
  isLoading,
  onRowAction,
  canEdit = true,
}: CheckInPatientTableProps) => {
  const columns = useMemo<GridColDef<CheckInPatient>[]>(
    () =>
      applyDefaultColumnSettings<CheckInPatient>([
        {
          field: "name",
          headerName: CHECK_IN_COLUMN_LABEL.patient,
          flex: 1,
          minWidth: 200,
          renderCell: ({ row }) => (
            <Flex alignItems="center" gap={1}>
              <Avatar
                sx={{
                  width: AVATAR_SIZE,
                  height: AVATAR_SIZE,
                  fontSize: "10px",
                  bgcolor: CHART_COLORS.bgSecondaryBlue,
                  color: CHART_COLORS.btnSecondaryText,
                }}
              >
                {getInitials(row.name)}
              </Avatar>

              <Flex flexDirection="column">
                <Text isTabletext>{row.name}</Text>
                {!!row.mrn && (
                  <Text
                    {...CHART_TYPOGRAPHY.body5}
                    c={CHART_COLORS.textMutedGrey}
                  >
                    {row.mrn}
                  </Text>
                )}
              </Flex>
            </Flex>
          ),
        },
        {
          field: "amount",
          headerName: CHECK_IN_COLUMN_LABEL.amount,
          flex: 0.8,
          minWidth: 120,
          renderCell: ({ row }) => (
            <Text isTabletext>
              {row.amount === undefined || row.amount === null
                ? "-"
                : `${CHECK_IN_AMOUNT_CURRENCY}${row.amount}`}
            </Text>
          ),
        },
        {
          field: "paymentStatus",
          headerName: CHECK_IN_COLUMN_LABEL.paymentStatus,
          flex: 1,
          minWidth: 140,
          renderCell: ({ row }) => (
            <StatusChip
              label={CHECK_IN_PAYMENT_STATUS_LABEL[row.paymentStatus]}
              variant={getCheckInStatusVariant(row.paymentStatus)}
            />
          ),
        },
        {
          field: "actions",
          headerName: "",
          flex: 1.2,
          minWidth: 280,
          align: "right",
          headerAlign: "right",
          renderCell: ({ row }) => {
            if (!canEdit) return null;

            return (
              <Flex gap={2} justifyContent="flex-end">
                {getCheckInRowActions(row.paymentStatus).map((action) => (
                  <Link
                    key={action}
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => onRowAction(action, row)}
                    sx={{
                      ...CHART_TYPOGRAPHY.body5,
                      fontFamily: CHART_TYPOGRAPHY.fontFamily,
                      fontWeight: CHART_TYPOGRAPHY.weight.medium,
                      color: CHART_COLORS.btnSecondaryText,
                    }}
                  >
                    {CHECK_IN_ROW_ACTION_LABEL[action]}
                  </Link>
                ))}
              </Flex>
            );
          },
        },
      ]),
    [onRowAction, canEdit]
  );

  return (
    <CustomDataGridV2
      rows={patients}
      columns={columns}
      getRowId={(row) => row.uuid}
      loading={isLoading}
      rowHeight={CHECK_IN_TABLE_ROW_HEIGHT}
      autoHeight
      hideFooter
      disableColumnMenu
      sx={{
        border: `1px solid ${CHART_COLORS.border}`,
        borderRadius: CHART_DIMENSIONS.borderRadius,
        backgroundColor: CHART_COLORS.surfaceWhite,
      }}
    />
  );
};

export default CheckInPatientTable;
